import { postDocFile, HEALTH_KEY } from '../../../lib/healthMask';
// Firma: tüm personelin eksik evrakları tek ekranda – solda seçilen dosyalar, sağda personel × evrak tablosu (sürükle-bırak), tek "Tümünü Yükle"
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { CheckCircle2, Upload, Loader2, FolderOpen, X, GripVertical, ChevronUp, ChevronDown, FileText, MousePointer2, Clock } from 'lucide-react';
import { confirmDialog } from '../../ConfirmHost';
import { EMPLOYEE_DOCS } from '../../employees/constants';
import { toItems, FileChip } from './BulkUploadDialog';
import { confirmPdfPages } from '../../../lib/pdfPageGuard';
import { PageLimitTag } from '../../documents/PageLimitTag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bırakılan öğelerden dosyaları toplar; klasörler (alt klasörler dahil) açılır. Entry'ler senkron alınmalı.
const readEntry = (entry) => new Promise((resolve) => {
  if (entry.isFile) { entry.file((f) => resolve([f]), () => resolve([])); return; }
  const reader = entry.createReader();
  const all = [];
  const next = () => reader.readEntries(async (batch) => {
    if (!batch.length) { resolve((await Promise.all(all.map(readEntry))).flat()); return; }
    all.push(...batch); next();
  }, () => resolve([]));
  next();
});
const droppedEntries = (dt) => Array.from(dt.items || []).map(it => it.webkitGetAsEntry?.()).filter(Boolean);
const hasFolder = (entries) => entries.some(en => en.isDirectory);
const collectFiles = async (entries) => (await Promise.all(entries.map(readEntry))).flat();

const W = 'flex h-12 w-[170px] items-center gap-1 overflow-hidden rounded-md px-1.5 text-[11px]';

// Hücre: personel sayfasındaki durumla aynı – onay bekliyor / onaylı / eksik / yüklenmedi; dosyaya tıklayınca görüntülenir
const MatrixCell = ({ st, file, uploading, onDropFile, onPickFile, onDelete, onView, testId }) => {
  const [over, setOver] = useState(false);
  const pickRef = useRef(null);
  if (uploading) return <div className={`${W} justify-center border-2 border-sky-300 bg-sky-50 text-sky-700`} data-testid={`${testId}-uploading`}><Loader2 className="h-3.5 w-3.5 animate-spin" />Yükleniyor</div>;
  if (st === 'kapsam_disi') return <div className={`${W} justify-center bg-slate-100 text-slate-400`} data-testid={`${testId}-na`}>Kapsam dışı</div>;
  const name = file?.original_filename || 'Belge';
  const droppable = !file || ['eksik', 'suresi_doldu'].includes(st);
  const tone = st === 'onay' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : !droppable ? 'bg-amber-50 text-amber-700 border border-amber-200' : '';
  if (st === 'onay' || !droppable) {
    const label = st === 'onay' ? 'Onaylı' : 'Onay bekliyor';
    const Icon = st === 'onay' ? CheckCircle2 : Clock;
    return (
      <div className={`${W} ${tone}`} title={file ? `${label} · ${name}` : label} data-testid={`${testId}-${st === 'onay' ? 'approved' : 'pending'}`}>
        <Icon className="h-3.5 w-3.5 shrink-0" />
        {file ? (
          <button type="button" onClick={onView} className="min-w-0 flex-1 truncate whitespace-nowrap text-left font-medium hover:underline" data-testid={`${testId}-view`}>{label} · {name}</button>
        ) : <span className="flex-1 font-medium">{label}</span>}
        {file && <button type="button" onClick={onDelete} className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-rose-100 hover:text-rose-600" title="Dosyayı sil" data-testid={`${testId}-delete`}><X className="h-3.5 w-3.5" /></button>}
      </div>
    );
  }
  const bad = file && ['eksik', 'suresi_doldu'].includes(st);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDropFile(e); }}
      className={`${W} border-2 transition-colors ${over ? 'border-sky-500 bg-sky-50' : bad ? 'border-dashed border-rose-400 bg-rose-50 text-rose-600' : 'border-dashed border-slate-300 bg-white text-slate-400'}`}
      data-testid={testId}
    >
      {bad ? (
        <button type="button" onClick={onView} className="min-w-0 flex-1 truncate text-left font-medium hover:underline" title="Eksik/Hatalı – görüntülemek için tıklayın, yeni sürümü buraya bırakın" data-testid={`${testId}-view`}>Eksik/Hatalı · yeni sürümü bırakın</button>
      ) : (
        <button type="button" onClick={() => pickRef.current?.click()} className="w-full text-center" data-testid={`${testId}-pick`}>
          <span className="md:hidden">Dosya seç</span><span className="hidden md:inline">Sürükle bırak</span>
        </button>
      )}
      <input ref={pickRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onPickFile(f); }} />
    </div>
  );
};

// Çoklu seçimde yükleme sırası: ada göre sıralı gelir, sürükleyerek veya oklarla değiştirilebilir
const OrderDialog = ({ items, setItems, targets, onCancel, onConfirm }) => {
  const [dragIdx, setDragIdx] = useState(null);
  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setItems(next);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl" data-testid="matrix-order-dialog">
        <DialogHeader><DialogTitle>Yükleme Sırası</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Dosyalar ada göre sıralandı. Sağdaki evrak sırası sabittir; soldaki dosyaları sürükleyerek veya oklarla yer değiştirin. Her dosya karşısındaki evraka yüklenecek.</p>
        <div className="max-h-[60vh] space-y-1.5 overflow-y-auto pr-1">
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-3 px-1 text-[11px] font-semibold text-slate-500">
            <span>Dosya (sırayı değiştirin)</span><span className="border-l border-slate-300 pl-3">Yükleneceği evrak</span>
          </div>
          {items.map((it, i) => (
            <div key={it.id}>
            {targets[i] && targets[i].person !== targets[i - 1]?.person && (
              <p className="mt-2 mb-1 border-b pb-0.5 text-xs font-semibold text-slate-700" data-testid={`matrix-order-person-${i}`}>{targets[i].person}</p>
            )}
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-center gap-x-3" data-testid={`matrix-order-row-${i}`}>
              <div draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); if (dragIdx !== null) move(dragIdx, i); setDragIdx(null); }}
                className={`flex min-w-0 items-center gap-2 rounded-md border bg-white px-2 py-1.5 text-xs cursor-grab hover:border-sky-400 ${dragIdx === i ? 'opacity-50' : ''}`}>
                <GripVertical className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="w-5 shrink-0 font-semibold text-slate-500">{i + 1}.</span>
                <span className="min-w-0 flex-1 truncate font-medium text-slate-800" title={it.file.name}>{it.file.name}</span>
                <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} className="rounded p-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30" data-testid={`matrix-order-up-${i}`}><ChevronUp className="h-4 w-4" /></button>
                <button type="button" onClick={() => move(i, i + 1)} disabled={i === items.length - 1} className="rounded p-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30" data-testid={`matrix-order-down-${i}`}><ChevronDown className="h-4 w-4" /></button>
              </div>
              <div className={`flex h-full min-w-0 items-center border-l-2 border-slate-300 pl-3 text-xs ${targets[i] ? 'text-sky-700' : 'text-rose-600'}`} title={targets[i]?.doc} data-testid={`matrix-order-target-${i}`}>
                <span className="truncate">{targets[i]?.doc || 'Boş evrak alanı yok, yüklenmeyecek'}</span>
              </div>
            </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="matrix-order-cancel">İptal</Button>
          <Button onClick={onConfirm} className="bg-emerald-600 hover:bg-emerald-700" data-testid="matrix-order-confirm">Bu Sırayla Yükle</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ID_KEY = 'id_card';
// Gelişmiş QR kimlik ön yüzü belge gibi davranır (durum: beklemede / onay / eksik)
const docState = (e, key) => key === ID_KEY
  ? { st: e?.id_card_path ? (['onay', 'eksik'].includes(e.id_card_status) ? e.id_card_status : 'beklemede') : 'beklemede', file: e?.id_card_path ? { original_filename: 'Kimlik ön yüzü' } : null }
  : { st: e?.documents?.[key] || 'beklemede', file: e?.document_files?.[key] };

export const MatrixUploadDialog = ({ entities, requiredDocs = [], files, setFiles, focusId, onClose, onUploaded, onDeleted, onView, idCard = false, onIdCard }) => {
  const DOCS = idCard ? [...EMPLOYEE_DOCS, { key: ID_KEY, label: 'Kimlik / Pasaport (QR)' }] : EMPLOYEE_DOCS;
  // Açılıştaki yüklenmesi gereken hücreler (boş veya eksik/hatalı); onaylı / onay bekleyenler gösterilmez
  const [needed] = useState(() => new Set(entities.flatMap(e => DOCS.map(d => `${e.id}:${d.key}`)).filter(c => {
    const [id, key] = c.split(':');
    const { st, file } = docState(entities.find(x => x.id === id), key);
    return st !== 'kapsam_disi' && st !== 'onay' && (!file || ['eksik', 'suresi_doldu'].includes(st));
  })));
  const rows = entities.filter(e => DOCS.some(d => needed.has(`${e.id}:${d.key}`))).map(e => ({ e }));
  const [done, setDone] = useState([]);
  const [busy, setBusy] = useState([]);
  const [zoneOver, setZoneOver] = useState(false);
  const inputRef = useRef(null);
  const [ordering, setOrdering] = useState(null);
  const cols = DOCS.filter(d => rows.some(r => needed.has(`${r.e.id}:${d.key}`))).map(d => ({ ...d, optional: d.key !== ID_KEY && requiredDocs.length > 0 && !requiredDocs.includes(d.key) }));
  const cellInfo = (cell) => {
    const [empId, key] = cell.split(':');
    const e = entities.find(x => x.id === empId);
    return { e, key, ...docState(e, key) };
  };
  const viewIdCard = (empId) => axios.get(`${API}/employees/${empId}/id-card`, { responseType: 'blob' }).then(r => window.open(URL.createObjectURL(r.data), '_blank')).catch(() => toast.error('Kimlik açılamadı'));
  const isDroppable = (cell) => { const { st, file } = cellInfo(cell); return st !== 'kapsam_disi' && (!file || ['eksik', 'suresi_doldu'].includes(st)) && st !== 'onay'; };

  useEffect(() => {
    if (focusId) setTimeout(() => document.querySelector(`[data-testid="matrix-row-${focusId}"]`)?.scrollIntoView({ block: 'center' }), 200);
  }, [focusId]);

  // Ekran açıkken dışarıdan sürüklenen dosya hiçbir yerde tarayıcıda açılmasın (hücre dışına bırakılırsa yok sayılır)
  useEffect(() => {
    const stop = (e) => { if (e.dataTransfer?.types?.includes('Files')) e.preventDefault(); };
    window.addEventListener('dragover', stop);
    window.addEventListener('drop', stop);
    return () => { window.removeEventListener('dragover', stop); window.removeEventListener('drop', stop); };
  }, []);

  const byId = (id) => files.find(f => f.id === id);
  const addFiles = (list, quiet) => { const items = toItems(list, quiet); setFiles(prev => [...prev, ...items]); return items; };
  const dropToList = async (e) => {
    const entries = droppedEntries(e.dataTransfer);
    if (!hasFolder(entries)) { addFiles(e.dataTransfer.files); return; }
    const all = await collectFiles(entries);
    const items = addFiles(all, true);
    toast.success(`Klasörden ${items.length} dosya eklendi${all.length > items.length ? ` (${all.length - items.length} uygun olmayan dosya atlandı)` : ''}`);
  };
  const removeFile = (fid) => setFiles(prev => prev.filter(f => f.id !== fid));
  // Hücreye bırakılan dosya anında yüklenir
  const dropOn = async (cell, e) => {
    let item = byId(e.dataTransfer.getData('text/plain'));
    if (!item && hasFolder(droppedEntries(e.dataTransfer))) { await dropToList(e); toast.info('Klasördeki dosyalar sol listeye eklendi; ilgili hücrelere sürükleyin'); return; }
    if (!item && e.dataTransfer.files?.length) item = addFiles([e.dataTransfer.files[0]])[0];
    if (!item || busy.includes(cell)) return;
    await uploadTo(cell, item);
  };

  const uploadTo = async (cell, item) => {
    const [empId, key] = cell.split(':');
    const isId = key === ID_KEY;
    if (isId && !/\.(jpe?g|png|webp)$/i.test(item.file.name)) { toast.error('Kimlik ön yüzü JPG/PNG/WEBP fotoğraf olmalı'); return; }
    if (!isId && key !== HEALTH_KEY && !(await confirmPdfPages(item.file, { collection: 'employees', docKey: key, label: cols.find(c => c.key === key)?.label }))) return;
    const body = new FormData();
    body.append('file', item.file);
    setBusy(prev => [...prev, cell]);
    try {
      if (isId) {
        await axios.put(`${API}/employees/${empId}/id-card`, body);
        onIdCard?.(empId, { id_card_path: 'uploaded', id_card_status: 'beklemede', id_card_note: '' });
      } else {
        const res = await postDocFile('employees', empId, key, item.file);
        onUploaded(empId, key, res.data);
      }
      setDone(prev => [...prev, cell]);
      setFiles(prev => prev.filter(f => f.id !== item.id));
      toast.success(`${cols.find(c => c.key === key)?.label} yüklendi`);
    } catch (err) {
      if (err.cancelled) return;
      const r = rows.find(x => x.e.id === empId);
      toast.error(`${r?.e.name} ${r?.e.surname} – ${cols.find(c => c.key === key)?.label}: ${err.response?.data?.detail || 'yüklenemedi'}`);
    } finally {
      setBusy(prev => prev.filter(c => c !== cell));
    }
  };

  // "Dosya Seç": seçilen dosyalar sırayla ilk boş evrak hücrelerine yüklenir
  const freeCells = () => rows.flatMap(r => cols.map(c => `${r.e.id}:${c.key}`)).filter(c => needed.has(c) && isDroppable(c) && !busy.includes(c));
  const autoFill = (items) => {
    const free = freeCells();
    if (items.length > free.length) toast.error(`${items.length - free.length} dosya için boş evrak alanı kalmadı, yüklenmedi`);
    items.slice(0, free.length).forEach((item, i) => uploadTo(free[i], item));
  };

  const cellTarget = (cell) => {
    const [empId, key] = cell.split(':');
    const r = rows.find(x => x.e.id === empId);
    return { person: `${r?.e.name} ${r?.e.surname}`, doc: cols.find(c => c.key === key)?.label };
  };

  const deleteCell = async (cell) => {
    const [empId, key] = cell.split(':');
    const { st } = cellInfo(cell);
    const label = cols.find(c => c.key === key)?.label;
    if (!(await confirmDialog({
      title: 'Belge silinsin mi?',
      description: st === 'onay' ? `${label} ONAYLI belgedir. Silerseniz onay kalkar ve belge yeniden yüklenip onaylanmalıdır.` : `${label} belgesi silinecek.`,
      confirmLabel: 'Evet, Sil',
    }))) return;
    setBusy(prev => [...prev, cell]);
    try {
      if (key === ID_KEY) {
        await axios.delete(`${API}/employees/${empId}/id-card`);
        onIdCard?.(empId, { id_card_path: null, id_card_status: null, id_card_note: null });
      } else {
        const res = await axios.delete(`${API}/employees/${empId}/document-files/${key}`);
        onDeleted(empId, key, res.data);
      }
      toast.success('Dosya silindi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dosya silinemedi');
    } finally {
      setBusy(prev => prev.filter(c => c !== cell));
    }
  };

  const close = () => { if (busy.length) { toast.error('Yükleme sürüyor, lütfen bekleyin'); return; } onClose(); };

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-[96vw] w-[96vw] h-[92dvh] flex flex-col overflow-hidden" onInteractOutside={(e) => e.preventDefault()} data-testid="matrix-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5" />Toplu Evrak Yükleme</DialogTitle>
        </DialogHeader>
        <p className="rounded-md border-2 border-rose-500 bg-rose-50 px-3 py-2 text-center text-base font-extrabold uppercase tracking-wide text-rose-700 sm:text-lg" data-testid="matrix-only-missing-notice">BU EKRANDA SADECE YÜKLENMESİ GEREKEN EVRAKLAR GÖSTERİLİR</p>
        <p className="text-xs text-slate-500 md:hidden">Tablodaki her evrak için "Dosya seç"e dokunun veya üstten birden çok dosya seçin; dosyalar sıradaki boş alanlara otomatik yüklenir.</p>
        <p className="hidden md:block text-xs text-slate-500">Soldaki dosyaları sağdaki tabloda ilgili personelin ilgili evrak hücresine sürükleyin. Bırakılan dosya anında yüklenir. Dosya adına tıklayınca görüntülenir, X ile silinir.</p>
        <div className="grid flex-1 min-h-0 grid-cols-1 grid-rows-[auto_minmax(0,1fr)] md:grid-rows-1 md:grid-cols-[320px_1fr] gap-3 md:gap-4">
          <div
            onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setZoneOver(true); } }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setZoneOver(false); }}
            onDrop={(e) => { if (!e.dataTransfer.types.includes('Files')) return; e.preventDefault(); setZoneOver(false); dropToList(e); }}
            className="flex min-h-0 max-h-[38vh] md:max-h-none flex-col gap-2 border-b pb-3 md:border-b-0 md:pb-0 md:border-r md:pr-3" data-testid="matrix-file-panel"
          >
            <div
              className={`rounded-lg border-2 border-dashed p-3 text-center transition-colors ${zoneOver ? 'border-sky-500 bg-sky-50' : 'border-slate-300 bg-slate-50'}`}
            >
              <Button type="button" size="sm" onClick={() => inputRef.current?.click()} className="bg-sky-600 text-white hover:bg-sky-700" data-testid="matrix-pick-files-btn">
                <FolderOpen className="mr-1 h-4 w-4" />Dosya Seç
              </Button>
              <p className="mt-1 text-[11px] text-slate-500">Seçilen dosyalar sıradaki boş evrak alanına otomatik yüklenir · veya dosyaları tablodaki ilgili hücreye sürükleyin · PDF, JPG, PNG, WEBP · en fazla 10 MB</p>
              <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { const items = toItems(e.target.files).sort((a, b) => a.file.name.localeCompare(b.file.name, 'tr', { numeric: true })); e.target.value = ''; if (items.length > 1) setOrdering(items); else autoFill(items); }} data-testid="matrix-file-input" />
            </div>
            <div className="flex-1 space-y-1.5 overflow-y-auto pr-1" data-testid="matrix-file-list">
              {files.map(f => <FileChip key={f.id} item={f} assignedLabel="" onRemove={() => removeFile(f.id)} />)}
              {!files.length && !done.length && (
                <div className="py-6" data-testid="matrix-dnd-demo">
                  <div className="relative mx-auto hidden md:block h-24 w-[230px]">
                    <div className="dnd-file absolute left-2 top-2 flex h-11 w-9 items-center justify-center rounded-md border border-rose-200 bg-white shadow-md">
                      <FileText className="h-5 w-5 text-rose-500" />
                      <MousePointer2 className="absolute -bottom-2 -right-2 h-4 w-4 fill-slate-800 text-white" />
                    </div>
                    <div className="dnd-target absolute bottom-1 right-0 flex h-10 w-24 items-center justify-center rounded-md border-2 border-dashed text-[10px] text-slate-400">
                      Sürükle bırak
                      <CheckCircle2 className="dnd-check absolute -right-2 -top-2 h-5 w-5 rounded-full bg-white text-emerald-600" />
                    </div>
                  </div>
                  <p className="mt-2 hidden md:block text-center text-xs text-slate-500">Masaüstünden dosyayı tutup tablodaki ilgili evrak hücresine bırakın, anında yüklenir.</p>
                </div>
              )}
            </div>
          </div>
          <div className="min-h-0 overflow-auto rounded-lg border" data-testid="matrix-table">
            {rows.length ? (
              <table className="w-full border-separate border-spacing-0 text-sm">
                <thead className="sticky top-0 z-10 bg-slate-100">
                  <tr>
                    <th className="sticky left-0 z-20 min-w-[180px] border-b bg-slate-100 px-3 py-2 text-left text-xs font-semibold text-slate-600">Personel</th>
                    {cols.map(c => (
                      <th key={c.key} className="min-w-[170px] border-b px-2 py-2 text-left text-xs font-semibold text-slate-600" data-testid={`matrix-col-${c.key}`}>
                        {c.key === 'mesleki_yeterlilik' ? 'Mesleki Yeterlilik (MYK)' : c.label}
                        <span className={`ml-1 text-[10px] ${c.optional ? 'text-emerald-700' : 'text-rose-600'}`}>{c.optional ? '(O)' : '(Z)'}</span>
                        {c.key !== ID_KEY && <div><PageLimitTag collection="employees" docKey={c.key} /></div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.e.id} className={i % 2 ? 'bg-slate-50' : 'bg-white'} data-testid={`matrix-row-${r.e.id}`}>
                      <td className={`sticky left-0 z-[5] border-b px-3 py-2 ${r.e.id === focusId ? 'bg-sky-100' : i % 2 ? 'bg-slate-50' : 'bg-white'}`}>
                        <p className="text-xs font-semibold text-slate-800"><span className="mr-1 text-slate-400">{i + 1}.</span>{r.e.name} {r.e.surname}</p>
                        {r.e.position && <p className="text-[10px] text-slate-500">{r.e.position}</p>}
                      </td>
                      {cols.map(c => {
                        const cell = `${r.e.id}:${c.key}`;
                        const { st, file } = cellInfo(cell);
                        return (
                          <td key={c.key} className="border-b px-2 py-1.5">
                            {needed.has(cell) ? <MatrixCell testId={`matrix-cell-${r.e.id}-${c.key}`} st={st} file={file} uploading={busy.includes(cell)}
                              onDropFile={(e) => dropOn(cell, e)} onPickFile={(f) => { const item = addFiles([f])[0]; if (item && !busy.includes(cell)) uploadTo(cell, item); }} onDelete={() => deleteCell(cell)} onView={() => (c.key === ID_KEY ? viewIdCard(r.e.id) : onView(r.e, c.key, c.label))} /> : <span className="block w-[170px] text-center text-[11px] text-slate-300">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="py-10 text-center text-sm text-slate-500" data-testid="matrix-no-missing">Yüklenmesi gereken evrak yok.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy.length > 0} data-testid="matrix-close-btn">Kapat</Button>
        </DialogFooter>
      </DialogContent>
      {ordering && (
        <OrderDialog items={ordering} setItems={setOrdering} targets={freeCells().map(cellTarget)}
          onCancel={() => setOrdering(null)} onConfirm={() => { autoFill(ordering); setOrdering(null); }} />
      )}
    </Dialog>
  );
};
